import { DataSource } from "./DataSource";

/**
 * A filterable value.
 */
export type FilterValue = string | boolean | number | object | ReadonlyArray<string | boolean | number | object> | null;

/**
 * A filter set.
 */
export interface FilterSet {
  /**
   * The full text search filter.
   */
  readonly full_text_search?: string | null;

  /**
   * Gets or sets the filter for the specified key.
   */
  readonly [key: string]: FilterValue | undefined;
}

/**
 * Specifies the filter methods.
 */
export interface FilterMethods {

  /**
   * Sets the filter used by the data source, clearing the original filter.
   * @param filter The filter to set.
   */
  setFilter(filter: FilterSet): void;

  /**
   * Filters the data by the specified name and value.
   * @param name The name of the column to filter.
   * @param value The value to filter by, or either null or an empty string to remove filtering on that column.
   */
  filterBy(name: "full_text_search", value: string | null): void;

  /**
   * Filters the data by the specified name and value.
   * @param name The name of the column to filter.
   * @param value The value to filter by, or either null or an empty string to remove filtering on that column.
   */
  filterBy(name: string, value: FilterValue): void;

  /**
   * Clears the filters.
   */
  clearFilter(): void;
}

/**
 * Specifies properties and methods for the filtering of data source data.
 */
export interface Filterable extends FilterMethods {

  /**
   * Gets the filter for the column with the specified name.
   * @param name The name of the column to get the current filter for.
   * @returns The current filter for the column, or null if no filter.
   */
  getFilter(name: "full_text_search"): string | null;

  /**
   * Gets the filter for the column with the specified name.
   * @param name The name of the column to get the current filter for.
   * @returns The current filter for the column, or null if no filter.
   */
  getFilter(name: string): FilterValue;

  /**
   * Gets the current filter set.
   * @returns The current filter set.
   */
  getFilter(): FilterSet;
}

/**
 * Indicates whether the specified DataSource implements the Filterable interface.
 * @param dataSource The datasource to test.
 * @returns True if the DataSource implements the Filterable interface, otherwise false.
 */
export function isFilterable(dataSource: DataSource<object>): dataSource is DataSource<object> & Filterable {
  if (!dataSource) return false;
  return Object.prototype.hasOwnProperty.call(dataSource, "setFilter")
  && Object.prototype.hasOwnProperty.call(dataSource, "filterBy")
  && Object.prototype.hasOwnProperty.call(dataSource, "clearFilter")
  && Object.prototype.hasOwnProperty.call(dataSource, "getFilter");
}
